import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { liveQuery, Subscription } from "dexie";
import { DateTime } from "luxon";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { Uuid } from "@/domains/global/identifiers";
import { SessionPreviousChats } from "@/store/sessions/types";
import {
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuContentList,
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
} from "@/mds";
import { formatDateString, isToday } from "@/domains/date/date";
import { groupLens } from "@/modules/timeline/group-lenses";
import { LensListItem } from "@/modules/timeline/types";

interface PreviousSessionsContentListOptions {
  onSelectSession?: (sessionId: Uuid) => void;
  selectedSessionId?: Uuid;
  selectionVariant?: MDSMenuItemSelectionVariant;
}

export class ClassicChatHistoryStore extends AppSubStore {
  private liveQuerySubscription: Subscription | null = null;
  private subscribedPreviousSessions: SessionPreviousChats[] = [];
  isHistoryModalOpen = false;
  selectedSessionId: Uuid | undefined = undefined;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<
      ClassicChatHistoryStore,
      "liveQuerySubscription" | "subscribedPreviousSessions" | "initializeLiveQuery"
    >(this, {
      liveQuerySubscription: observable,
      subscribedPreviousSessions: observable,
      initializeLiveQuery: false,
      previousChatSessions: computed,
      previousSessionsContentList: computed,
      buildPreviousSessionsContentList: false,
      isHistoryModalOpen: observable,
      selectedSessionId: observable,
      openHistoryModal: action,
      closeHistoryModal: action,
      setSelectedSessionId: action,
      deleteSession: action,
    });

    this.initializeLiveQuery();
  }

  private initializeLiveQuery() {
    this.liveQuerySubscription?.unsubscribe();
    this.liveQuerySubscription = liveQuery(() => {
      return this.store.sessions.getPreviousChatSessions();
    }).subscribe({
      next: rows => {
        runInAction(() => {
          this.subscribedPreviousSessions = rows;
        });
      },
    });
  }

  get previousChatSessions(): SessionPreviousChats[] {
    return this.subscribedPreviousSessions;
  }

  get previousSessionsContentList(): MDSMenuContentList {
    return this.buildPreviousSessionsContentList();
  }

  openHistoryModal = (sessionId?: Uuid) => {
    this.isHistoryModalOpen = true;
    if (sessionId) {
      this.selectedSessionId = sessionId;
    }
  };

  closeHistoryModal = () => {
    this.isHistoryModalOpen = false;
  };

  setSelectedSessionId = (sessionId: Uuid | undefined) => {
    this.selectedSessionId = sessionId;
  };

  deleteSession = async (sessionId: Uuid) => {
    if (this.selectedSessionId === sessionId) {
      this.selectedSessionId = undefined;
    }

    await this.store.sessions.deleteSession({ sessionId });
  };

  buildPreviousSessionsContentList({
    onSelectSession,
    selectedSessionId,
    selectionVariant,
  }: PreviousSessionsContentListOptions = {}): MDSMenuContentList {
    const lensItems: LensListItem<SessionPreviousChats>[] = this.subscribedPreviousSessions.map(
      session => ({
        dateTime: new Date(session.lastUserActionAt),
        item: session,
      })
    );

    const groupedSessions = groupLens(lensItems);
    const items: MDSMenuContentItem[] = [];

    groupedSessions.forEach((group, groupIndex) => {
      if (groupIndex > 0) {
        items.push({
          id: `${group.title}-separator`,
          kind: MDSMenuContentItemKind.Divider,
        });
      }

      items.push({
        id: group.title,
        kind: MDSMenuContentItemKind.Header,
        label: group.title,
      });

      group.items.forEach(({ dateTime, item }) => {
        const dTime = DateTime.fromJSDate(dateTime);
        items.push({
          id: item.modelId,
          kind: MDSMenuContentItemKind.Item,
          variant: MDSMenuItemVariant.TwoLine,
          label: item.title,
          subtitle: isToday(dTime)
            ? formatDateString(dTime, "h:mm a")
            : formatDateString(dTime, "MMM dd"),
          isSelected: selectedSessionId === item.modelId,
          selectionVariant,
          rightAddon: () => (
            <MDSIconButton
              aria-label={`Delete ${item.title}`}
              data-classic-chat-history-delete-action
              iconKind={MDSIconKind.Trash}
              size={MDSIconButtonSize.XSmall}
              variant={MDSIconButtonVariant.Transparent}
              onClick={event => {
                event.stopPropagation();
                void this.deleteSession(item.modelId);
              }}
            />
          ),
          onClick: () => onSelectSession?.(item.modelId),
        });
      });
    });

    return items;
  }
}
